import { z } from 'zod';

import { BillType } from '../enums/billType';
import { messagingVolumeWithCostSchema, openAiUsageRowSchema } from './admin';
import { dateRangeQuerySchema } from './common';

// ============================================
// Daily Engagement Analytics Schemas
// ============================================

export const dailyEngagementQuerySchema = dateRangeQuerySchema;

export const dailyEngagementRowSchema = z.object({
  date: z.string(),
  uniqueDailyActiveUsers: z.number(),
  newActiveUsers: z.number(),
  totalMessages: z.number(),
  campaignMessages: z.number(),
});

export const dailyEngagementResponseSchema = z.array(dailyEngagementRowSchema);

// Types
export type DailyEngagementQuery = z.infer<typeof dailyEngagementQuerySchema>;
export type DailyEngagementRow = z.infer<typeof dailyEngagementRowSchema>;
export type DailyEngagementResponse = z.infer<
  typeof dailyEngagementResponseSchema
>;

// ============================================
// Cost Analytics Schemas
// ============================================

/*
 * `business` (default) = the session's active business only — the historical
 * behaviour. `org` = every business in the active business's org; an employee
 * must have access to all of them (403 otherwise), API keys always do.
 */
export const costAnalyticsScopeSchema = z.enum(['business', 'org']);

export const costAnalyticsQuerySchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
  scope: costAnalyticsScopeSchema.optional().default('business'),
});

export const categoryCostBreakdownSchema = z.object({
  messages: z.number(),
  cost: z.number(),
});

/*
 * A bill line plus the delivery counts for the same (date, country, category).
 *
 * Volume/unitPrice/cost come from the billing pipeline; the status counts are
 * always read from ClickHouse, because Meta's pricing_analytics has no delivery
 * dimension. The two therefore come from DIFFERENT ledgers and need not agree:
 * the counts are undefined when our DB has no record of the group at all (a
 * business that sends through its own Cloud API — render 'N/A', never 0, which
 * would wrongly assert nothing was delivered), and a row can carry counts with
 * volume 0 when ClickHouse saw traffic the bill did not charge for (e.g. a day
 * where every message failed).
 *
 * Counts are cumulative funnel values, so sentCount >= deliveredCount:
 * sentCount counts messages that reached 'sent' or beyond, deliveredCount those
 * that reached 'delivered' or beyond (read/clicked/responded roll up).
 */
/*
 * Which business a row belongs to. Set only on org-scoped responses, where
 * rows from several businesses share one table and the name alone may not
 * identify them (org business names repeat). Business-scoped responses leave
 * all three undefined, so their row shape is unchanged. `phone` is the
 * business's WhatsApp number with its calling code ('+91…'), null when the
 * business has no number on file.
 *
 * Spread into every row schema the org view tags, so the tables can render
 * one consistent pair of Business / Phone columns.
 */
const businessTagFields = {
  businessId: z.number().optional(),
  businessName: z.string().optional(),
  phone: z.string().nullable().optional(),
};

export const costAnalyticsVolumeRowSchema =
  messagingVolumeWithCostSchema.extend({
    sentCount: z.number().optional(),
    deliveredCount: z.number().optional(),
    failedCount: z.number().optional(),
    ...businessTagFields,
  });

/*
 * Messaging volume part of cost analytics. Volume is sourced through the SAME
 * pipeline that generates bills (fetchMessagingVolumeByType), so totals match
 * the invoice; `volumeRows` are the bill's per-country/category lines (there is
 * deliberately NO template dimension — Meta pricing_analytics doesn't have one,
 * and per-template delivery stats already live in template analytics).
 */
export const costAnalyticsMessagingSchema = z.object({
  volumeRows: z.array(costAnalyticsVolumeRowSchema),
  totalMessagingCost: z.number(),
  categoryBreakdown: z.object({
    MARKETING: categoryCostBreakdownSchema,
    UTILITY: categoryCostBreakdownSchema,
    AUTHENTICATION: categoryCostBreakdownSchema,
  }),
  /*
   * How volume was actually fetched: 'standard' = ClickHouse standard scan;
   * 'conservative' = Meta pricing_analytics with ClickHouse conservative
   * fallback (KARIX orgs also resolve as conservative in cost analytics —
   * the Karix CSV only exists at cycle end).
   */
  volumeSource: z.nativeEnum(BillType).optional(),
});

/*
 * One row of the template-wise cost tab: a (template, category, IST day)
 * group counted from OUR ClickHouse messages — an approximation by design.
 * Bills for CONSERVATIVE/KARIX orgs are sourced from Meta pricing_analytics,
 * which has no template dimension, so a template breakdown can only ever come
 * from traffic that went through Heltar. `billableCount` applies the same
 * filter the org's bill type would use on a ClickHouse scan, and
 * `estimatedCost` prices it per country at the current rates; neither is
 * promised to sum to the invoice (the Messaging tab is the exact view).
 */
export const costAnalyticsTemplateRowSchema = z.object({
  date: z.string(),
  templateName: z.string(),
  category: z.string().nullable(),
  sentCount: z.number(),
  deliveredCount: z.number(),
  failedCount: z.number(),
  billableCount: z.number(),
  estimatedCost: z.number(),
});

/*
 * One row of the template+country view: the same (template, category, IST day,
 * country) group the rolled-up view sums away — kept per-country so the unit
 * price for that country/category is displayable (the rolled-up row mixes
 * countries, so it can only show a total). Derived from the same single
 * ClickHouse fetch as `rows`; per-row
 * estimatedCost is rounded independently, so summing countryRows can differ
 * from the rolled row by paise.
 */
export const costAnalyticsTemplateCountryRowSchema =
  costAnalyticsTemplateRowSchema.extend({
    isoCode: z.string(),
    unitPrice: z.number(),
  });

export const costAnalyticsTemplateResponseSchema = z.object({
  rows: z.array(costAnalyticsTemplateRowSchema),
  countryRows: z.array(costAnalyticsTemplateCountryRowSchema),
  totalEstimatedCost: z.number(),
});

/*
 * One row of the day-wise AI usage view: a (model, IST day) group from
 * token_usage. `date` is an IST calendar label (YYYY-MM-DD), not an instant.
 */
export const costAnalyticsAiDailyRowSchema = openAiUsageRowSchema.extend({
  date: z.string(),
  ...businessTagFields,
});

/*
 * One row of the model-wise AI usage view. Business scope groups by model
 * alone (the historical shape); org scope groups by (business, model) and
 * tags each row, so the model-wise table breaks down per business exactly as
 * the day-wise one does.
 */
export const costAnalyticsAiModelRowSchema =
  openAiUsageRowSchema.extend(businessTagFields);

/*
 * AI (token) usage part of cost analytics. Both views come from ONE day-wise
 * query: `rows` (the model-wise view) is derived by summing `dailyRows` per
 * model — per (business, model) under org scope — so the two always reconcile
 * with each other and with the totals.
 */
export const costAnalyticsAiUsageSchema = z.object({
  rows: z.array(costAnalyticsAiModelRowSchema),
  dailyRows: z.array(costAnalyticsAiDailyRowSchema),
  totalTokens: z.number(),
  totalCostUsd: z.number(),
  totalCostInr: z.number(),
  usdToInrRate: z.number(),
});

/*
 * One business's share of an org-scoped response: the same headline numbers
 * the summary and category cards show for a single business, so the org view
 * can list them per business. `phone` is the business's WhatsApp number with
 * its calling code ('+91…'), null when the business has no number on file.
 */
export const costAnalyticsBusinessSummarySchema = z.object({
  businessId: z.number(),
  name: z.string(),
  phone: z.string().nullable(),
  categoryBreakdown: z.object({
    MARKETING: categoryCostBreakdownSchema,
    UTILITY: categoryCostBreakdownSchema,
    AUTHENTICATION: categoryCostBreakdownSchema,
  }),
  billableMessages: z.number(),
  totalMessagingCost: z.number(),
  aiCostInr: z.number(),
  grandTotalCost: z.number(),
});

// Combined response: messaging volume + AI usage + grand total (INR)
export const costAnalyticsResponseSchema = costAnalyticsMessagingSchema.extend({
  aiUsage: costAnalyticsAiUsageSchema,
  grandTotalCost: z.number(),
  /*
   * Org scope only — absent on business-scoped responses so that shape stays
   * exactly what it always was. Sums to the top-level totals.
   */
  businesses: z.array(costAnalyticsBusinessSummarySchema).optional(),
});

// Types
export type CostAnalyticsScope = z.infer<typeof costAnalyticsScopeSchema>;
export type CostAnalyticsQuery = z.infer<typeof costAnalyticsQuerySchema>;
export type CostAnalyticsBusinessSummary = z.infer<
  typeof costAnalyticsBusinessSummarySchema
>;
export type CostAnalyticsVolumeRow = z.infer<
  typeof costAnalyticsVolumeRowSchema
>;
export type CostAnalyticsMessaging = z.infer<
  typeof costAnalyticsMessagingSchema
>;
export type CostAnalyticsAiDailyRow = z.infer<
  typeof costAnalyticsAiDailyRowSchema
>;
export type CostAnalyticsAiModelRow = z.infer<
  typeof costAnalyticsAiModelRowSchema
>;
export type CostAnalyticsAiUsage = z.infer<typeof costAnalyticsAiUsageSchema>;
export type CostAnalyticsResponse = z.infer<typeof costAnalyticsResponseSchema>;
export type CostAnalyticsTemplateRow = z.infer<
  typeof costAnalyticsTemplateRowSchema
>;
export type CostAnalyticsTemplateCountryRow = z.infer<
  typeof costAnalyticsTemplateCountryRowSchema
>;
export type CostAnalyticsTemplateResponse = z.infer<
  typeof costAnalyticsTemplateResponseSchema
>;
